import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { kycApi } from '@/api/kyc';
import { Container, Flex } from '@/ui/layout';
import { Body2Light, Body3Light } from '@/ui/typography';
import { adminKYCApi } from '@/api/admin/kyc';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@/components';
import { Checkbox, Modal } from 'antd';
import { usePopups } from '@/hooks';

export const AddQuestionnaireModal = ({
  visible = false,
  onClose,
  user,
  onSuccess,
  referralToken
}) => {
  switch (user) {
    case 'investor': {
      return <AddModalInvestor visible={visible} onClose={onClose} onSuccess={onSuccess} />;
    }
    case 'referral': {
      return (
        <AddModalInvestor
          referralView
          referralToken={referralToken}
          visible={visible}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      );
    }
    case 'admin': {
      return <AddModalAdmin visible={visible} onClose={onClose} onSuccess={onSuccess} />;
    }
    default:
      return null;
  }
};

const AddModalInvestor = ({ onSuccess, referralView, referralToken, visible, ...props }) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const { data, refetch } = useQuery({
    queryKey: ['getAllowedQuestsToAdd', referralToken],
    queryFn: referralView
      ? () => kycApi.getReferralAllowedQuestsToAdd(referralToken)
      : kycApi.getAllowedQuestsToAdd,
    enabled: (referralView && !!referralToken) || !referralView
  });
  const { mutateAsync } = useMutation({
    mutationFn: referralView ? kycApi.requestReferralQuest : kycApi.requestQuest
  });

  useEffect(() => {
    if (visible) refetch();
  }, [visible]);

  const allowedQuestsList = useMemo(() => (data ? data.data : null), [data]);

  const onAdd = async (questId) => {
    try {
      (await (referralView && referralToken))
        ? mutateAsync({
            token: referralToken,
            questId: questId
          })
        : mutateAsync({ questId });
      if (onSuccess) {
        await onSuccess();
        showSuccessToast('Added successfully');
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  return (
    <AddModal allowedQuestsList={allowedQuestsList} onAdd={onAdd} visible={visible} {...props} />
  );
};

const AddModalAdmin = ({ onSuccess, visible, ...props }) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const { id } = useParams();
  const { data, refetch } = useQuery({
    queryKey: ['getAllowedQuestsToAddAdmin'],
    queryFn: () => adminKYCApi.getAllowedQuestsToAdd({ investorId: id })
  });
  const { mutateAsync } = useMutation({ mutationFn: adminKYCApi.requestQuest });

  useEffect(() => {
    if (visible) refetch();
  }, [visible]);

  const allowedQuestsList = useMemo(() => (data ? data.data : null), [data]);

  const onAdd = async (questId, onlyAdmin) => {
    try {
      await mutateAsync({ investorId: id, questId: questId, onlyAdmin: onlyAdmin });
      if (onSuccess) {
        await onSuccess();
        showSuccessToast('Added successfully');
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  return (
    <AddModal
      allowedQuestsList={allowedQuestsList}
      onAdd={onAdd}
      visible={visible}
      adminView={true}
      {...props}
    />
  );
};

const AddModal = ({ allowedQuestsList, onAdd, visible, onClose, adminView }) => {
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = usePopups();
  const [selected, setSelected] = useState(null);
  const [onlyAdmin, setOnlyAdmin] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);

  const onAddQuest = async () => {
    setDisabledButton(true);
    try {
      await onAdd(allowedQuestsList.find((quest) => quest.id === selected)?.id, onlyAdmin);
      setSelected(null);
      onClose(false);
      showSuccessToast('Successfully added');
      setDisabledButton(false);
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
      setDisabledButton(false);
    }
  };

  return (
    <Modal open={visible} onCancel={() => onClose(false)} closable footer={null} centered>
      <Flex $paddingTop={30} gap={15} vertical>
        {allowedQuestsList && allowedQuestsList.length > 0 ? (
          <Select
            allowClear
            placeholder={t('Questionnaires allowed to add')}
            options={allowedQuestsList.map((item) => {
              return {
                label: item.name ? String(item.name).toLocaleLowerCase() : `${t('No Name')}`,
                value: item.id
              };
            })}
            value={selected}
            onChange={(value) => setSelected(value)}
          />
        ) : (
          <Body2Light>{t('Not allowed questionnaires to add')}</Body2Light>
        )}
        {adminView && (
          <Body3Light>
            <Checkbox
              name='OnlyAdmin'
              value={onlyAdmin}
              checked={onlyAdmin}
              onChange={(e) => setOnlyAdmin(e.target.checked)}
              style={{ fontSize: '15px' }}
            >
              {t('Only Admin')}
            </Checkbox>
          </Body3Light>
        )}
      </Flex>

      <Flex justify='flex-end'>
        <Container $marginTop={8} $width={120}>
          <Button text={t('Add')} onClick={onAddQuest} disabled={!selected || disabledButton} />
        </Container>
      </Flex>
    </Modal>
  );
};
